import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EmployeesService, PatchUserBody } from '../../services/employee.service';
import {
  Company,
  CountryPhoneCode,
  Department,
  Employee,
  EmployeeLeavePolicyAssignment,
  PayGroup,
  Person,
  Shift,
  WorkOrder,
  UserWorkOrderAssignment,
  WorkerType,
} from '../../models/employee.model';
import {
  SearchSelectComponent,
  SearchSelectOption,
} from '../search-select/search-select.component';

type EmployeeTab = 'Contact' | 'Work' | 'Schedule' | 'Reports';
type EmployeeStatus = 'Active' | 'Inactive' | 'Terminated' | 'OnLeave';
type ToastKind = 'success' | 'error' | 'info';
type AdminAccessValue = 'standard' | 'admin';

type RequiredEmployeeField =
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'employeeNumber'
  | 'phoneLocalNumber'
  | 'rfid'
  | 'reportToId'
  | 'companyId'
  | 'departmentId'
  | 'workerTypeId'
  | 'payGroupId'
  | 'defaultWorkOrderNumberId'
  | 'shiftId'
  | 'scheduleGroupId'
  | 'sickLeavePolicy'
  | 'vacationLeavePolicy';

interface EmployeeEditForm {
  email: string;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  phoneCountryIso2: string;
  phoneLocalNumber: string;
  rfid: string;
  username: string;
  departmentId: number | null;
  reportToId: number | null;
  payGroupId: number | null;
  workerTypeId: number | null;
  defaultWorkOrderNumberId: number | null;
  companyId: number | null;
  shiftId: number | null;
  status: EmployeeStatus;
  adminAccess: AdminAccessValue;
  enableAutoBooking: boolean;
}

const REQUIRED_FIELDS_BY_TAB: Record<EmployeeTab, RequiredEmployeeField[]> = {
  Contact: ['email', 'firstName', 'lastName', 'employeeNumber', 'phoneLocalNumber', 'rfid', 'reportToId'],
  Work: [
    'companyId',
    'departmentId',
    'workerTypeId',
    'payGroupId',
    'defaultWorkOrderNumberId',
    'shiftId',
    'sickLeavePolicy',
    'vacationLeavePolicy',
  ],
  Schedule: [],
  Reports: [],
};

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

@Component({
  selector: 'app-employee-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SearchSelectComponent],
  templateUrl: './employee-profile.component.html',
  styleUrls: ['./employee-profile.component.scss'],
})
export class EmployeeProfileComponent implements OnInit, OnDestroy {
  employeeId!: number;
  employee: Employee | null = null;

  private readonly destroy$ = new Subject<void>();

  isLoading = true;
  errorMessage: string | null = null;

  tabs: EmployeeTab[] = ['Contact', 'Work', 'Schedule', 'Reports'];
  activeTab: EmployeeTab = 'Contact';

  todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  saveError: string | null = null;

  form: EmployeeEditForm | null = null;
  private originalForm: EmployeeEditForm | null = null;

  isSaving = false;
  isUpdatingAdminAccess = false;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  departments: Department[] = [];
  employeesForReportTo: Person[] = [];
  payGroups: PayGroup[] = [];
  workerTypes: WorkerType[] = [];
  overheadWorkOrders: WorkOrder[] = [];
  companies: Company[] = [];
  shifts: Shift[] = [];
  countryPhoneCodes: CountryPhoneCode[] = [];


  assignedWorkOrders: UserWorkOrderAssignment[] = [];
  allWorkOrders: WorkOrder[] = [];

  isAddWonsModalOpen = false;
  isAddWonsConfirmModalOpen = false;
  isDeleteWonConfirmModalOpen = false;

  isAddingWons = false;
  addWonsError: string | null = null;
  wonSearchTerm = '';
  selectedWorkOrderIds = new Set<number>();

  pendingDeleteWorkOrder: UserWorkOrderAssignment | null = null;
  deletingWorkOrderNumberId: number | null = null;

  isDeleteModalOpen = false;
  isDeleting = false;
  deleteError: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly employeeService: EmployeesService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  get isDirty(): boolean {
    if (!this.form || !this.originalForm) return false;
    return JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
  }

  get statusOptions(): EmployeeStatus[] {
    return ['Active', 'Inactive', 'Terminated', 'OnLeave'];
  }

  get adminAccessOptions(): Array<{ value: AdminAccessValue; label: string }> {
    return [
      { value: 'standard', label: 'Standard User' },
      { value: 'admin', label: 'Admin' },
    ];
  }

  get autoBookingOptions(): Array<{ value: boolean; label: string }> {
    return [
      { value: true, label: 'Enabled' },
      { value: false, label: 'Disabled' },
    ];
  }

  hasNoSchedule(): boolean {
    const schedules = this.employee?.departmentScheduleGroup?.scheduleGroup?.schedules;
    return !Array.isArray(schedules) || schedules.length === 0;
  }

  getMissingFieldsForTab(tab: EmployeeTab): RequiredEmployeeField[] {
    if (tab === 'Schedule') {
      return this.hasNoSchedule() ? ['scheduleGroupId'] : [];
    }

    const fields = REQUIRED_FIELDS_BY_TAB[tab] ?? [];
    return fields.filter((field) => this.isMissingRequiredField(field));
  }

  hasMissingRequiredData(tab: EmployeeTab): boolean {
    return this.getMissingFieldsForTab(tab).length > 0;
  }

  getMissingFieldLabelsForTab(tab: EmployeeTab): string[] {
    return this.getMissingFieldsForTab(tab).map((field) => this.getFieldLabel(field));
  }

  private getFieldLabel(field: RequiredEmployeeField): string {
    const labels: Record<RequiredEmployeeField, string> = {
      email: 'Email',
      firstName: 'First Name',
      lastName: 'Last Name',
      employeeNumber: 'Employee #',
      phoneLocalNumber: 'Phone',
      rfid: 'RFID',
      reportToId: 'Reports To',
      companyId: 'Company',
      departmentId: 'Department',
      workerTypeId: 'Worker Type',
      payGroupId: 'Pay Group',
      defaultWorkOrderNumberId: 'Default Overhead WON',
      shiftId: 'Shift',
      scheduleGroupId: 'Schedule',
      sickLeavePolicy: 'Sick Leave Policy',
      vacationLeavePolicy: 'Vacation Leave Policy',
    };

    return labels[field];
  }

  get selectedPhoneCountry(): CountryPhoneCode | null {
    const currentForm = this.form;
    if (!currentForm?.phoneCountryIso2) return null;

    return this.countryPhoneCodes.find((country) => country.iso2 === currentForm.phoneCountryIso2) ?? null;
  }

  get phoneDisplayValue(): string {
    return this.employeeService.formatPhoneForDisplay(this.employee?.phoneNumber ?? null) || '-';
  }

  get isCurrentEmployeeAdmin(): boolean {
    return this.employeeService.hasAdminRole(this.employee);
  }

  get sickLeavePolicyName(): string {
    return this.employee?.sickLeavePolicy?.leavePolicy?.name ?? '-';
  }

  get vacationLeavePolicyName(): string {
    return this.employee?.vacationLeavePolicy?.leavePolicy?.name ?? '-';
  }

  get sickLeavePolicyStartDate(): string {
    return this.formatDate(this.employee?.sickLeavePolicy?.startDate);
  }

  get vacationLeavePolicyStartDate(): string {
    return this.formatDate(this.employee?.vacationLeavePolicy?.startDate);
  }

  get sickLeavePolicyHoursPerYear(): string {
    return this.formatHours(this.employee?.sickLeavePolicy?.leavePolicy?.hoursPerYear);
  }

  get vacationLeavePolicyHoursPerYear(): string {
    return this.formatHours(this.employee?.vacationLeavePolicy?.leavePolicy?.hoursPerYear);
  }

  get sickLeavePolicyMaxCarryOver(): string {
    return this.formatHours(this.employee?.sickLeavePolicy?.leavePolicy?.maxCarryOver);
  }

  get vacationLeavePolicyMaxCarryOver(): string {
    return this.formatHours(this.employee?.vacationLeavePolicy?.leavePolicy?.maxCarryOver);
  }

  get sickLeaveBalanceDisplay(): string {
    return this.getLeaveBalanceDisplay('Sick');
  }

  get vacationLeaveBalanceDisplay(): string {
    return this.getLeaveBalanceDisplay('Vacation');
  }

  get sickLeaveUsedDisplay(): string {
    return this.getLeaveUsedDisplay('Sick');
  }

  get vacationLeaveUsedDisplay(): string {
    return this.getLeaveUsedDisplay('Vacation');
  }

  ngOnInit(): void {
    this.countryPhoneCodes = this.employeeService.getCountryPhoneCodes();

    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const idParam = params.get('id');
        const id = Number(idParam);

        if (!idParam || Number.isNaN(id) || id <= 0) {
          this.employeeId = 0;
          this.employee = null;
          this.form = null;
          this.originalForm = null;
          this.errorMessage = 'Invalid employee ID';
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        if (this.employeeId === id && this.employee) {
          return;
        }

        this.employeeId = id;
        this.resetProfileStateForNavigation();
        void this.loadAll();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.unlockBodyScroll();
  }

  private resetProfileStateForNavigation(): void {
    this.employee = null;
    this.form = null;
    this.originalForm = null;
    this.errorMessage = null;
    this.saveError = null;
    this.deleteError = null;
    this.isDeleteModalOpen = false;
    this.isDeleting = false;
    this.isSaving = false;
    this.assignedWorkOrders = [];
    this.allWorkOrders = [];
    this.isAddWonsModalOpen = false;
    this.isAddingWons = false;
    this.addWonsError = null;
    this.wonSearchTerm = '';
    this.selectedWorkOrderIds.clear();
    this.deletingWorkOrderNumberId = null;
    this.activeTab = 'Contact';
    this.clearToast();
  }

  private async loadAll(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    try {
      const [
        employee,
        companies,
        departments,
        employees,
        payGroups,
        workerTypes,
        overheadWorkOrders,
        shifts,
        assignedWorkOrders,
        allWorkOrders,
      ] = await Promise.all([
        firstValueFrom(this.employeeService.getEmployeeById(this.employeeId)),
        firstValueFrom(this.employeeService.getAllCompanies()),
        firstValueFrom(this.employeeService.getAllDepartments()),
        firstValueFrom(this.employeeService.getAllEmployees(200)),
        firstValueFrom(this.employeeService.getPayGroups()),
        firstValueFrom(this.employeeService.getWorkerTypes()),
        firstValueFrom(this.employeeService.getOverheadWorkOrders()),
        firstValueFrom(this.employeeService.getShifts()),
        firstValueFrom(this.employeeService.getEmployeeAssignedWorkOrders(this.employeeId)),
        firstValueFrom(this.employeeService.getAllWorkOrders()),
      ]);

      if (!employee) {
        this.employee = null;
        this.form = null;
        this.originalForm = null;
        this.errorMessage = 'Employee not found';
        return;
      }

      this.employee = employee;
      this.companies = companies;
      this.departments = departments;
      this.employeesForReportTo = employees.filter((person) => person.id !== employee.id);
      this.payGroups = payGroups;
      this.workerTypes = workerTypes;
      this.overheadWorkOrders = overheadWorkOrders;
      this.shifts = shifts;
      this.assignedWorkOrders = assignedWorkOrders;
      this.allWorkOrders = allWorkOrders;

      this.buildFormFromEmployee(employee);
    } catch {
      this.errorMessage = 'Failed to load employee data';
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async reloadEmployeeProfile(): Promise<void> {
    const employee = await firstValueFrom(this.employeeService.getEmployeeById(this.employeeId));

    if (!employee) {
      this.showToast('error', 'Saved, but failed to reload the employee profile.');
      return;
    }

    this.employee = employee;
    this.buildFormFromEmployee(employee);
    this.cdr.detectChanges();
  }

  private buildFormFromEmployee(employee: Employee): void {
    const normalizedPhone = (employee.phoneNumber ?? '').replace(/\D/g, '');

    let phoneCountryIso2 = 'US';
    let phoneLocalNumber = '';

    if (normalizedPhone) {
      if (normalizedPhone.length === 10) {
        phoneLocalNumber = normalizedPhone;
      } else if (normalizedPhone.length === 11 && normalizedPhone.startsWith('1')) {
        phoneLocalNumber = normalizedPhone.slice(1);
      } else {
        const sortedCountries = [...this.countryPhoneCodes].sort(
          (a, b) => b.dialCode.length - a.dialCode.length,
        );

        const matchedCountry =
          sortedCountries.find((country) => normalizedPhone.startsWith(country.dialCode)) ?? null;

        if (matchedCountry) {
          phoneCountryIso2 = matchedCountry.iso2;
          phoneLocalNumber = normalizedPhone.slice(matchedCountry.dialCode.length);
        } else {
          phoneLocalNumber = normalizedPhone;
        }
      }
    }

    const nextForm: EmployeeEditForm = {
      email: employee.email ?? '',
      firstName: employee.firstName ?? '',
      lastName: employee.lastName ?? '',
      employeeNumber: employee.employeeNumber ?? '',
      phoneCountryIso2,
      phoneLocalNumber,
      rfid: employee.rfid ?? '',
      username: employee.username ?? '',
      companyId: employee.company?.id ?? null,
      departmentId: employee.department?.id ?? null,
      reportToId: employee.reportTo?.id ?? null,
      payGroupId: employee.payGroup?.id ?? null,
      workerTypeId: employee.workerType?.id ?? null,
      defaultWorkOrderNumberId: employee.defaultWorkOrderNumber?.id ?? null,
      shiftId: employee.shift?.id ?? null,
      status: (employee.employeeStatus as EmployeeStatus) ?? 'Active',
      adminAccess: this.employeeService.hasAdminRole(employee) ? 'admin' : 'standard',
      enableAutoBooking: !!employee.enableAutoBooking,
    };

    this.form = { ...nextForm };
    this.originalForm = { ...nextForm };
  }

  isMissingRequiredField(field: RequiredEmployeeField): boolean {
    if (field === 'sickLeavePolicy') {
      return !this.employee?.sickLeavePolicy?.leavePolicy;
    }

    if (field === 'vacationLeavePolicy') {
      return !this.employee?.vacationLeavePolicy?.leavePolicy;
    }

    if (!this.form) return false;

    const value = this.form[field as keyof EmployeeEditForm];

    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim().length === 0;
    }

    return false;
  }

  goBack(): void {
    void this.router.navigate(['/employees']);
  }

  openDeleteModal(): void {
    this.deleteError = null;
    this.isDeleteModalOpen = true;
    this.syncBodyScrollLock();
  }

  closeDeleteModal(): void {
    if (this.isDeleting) return;

    this.deleteError = null;
    this.isDeleteModalOpen = false;
    this.syncBodyScrollLock();
  }

  async confirmDeleteUser(): Promise<void> {
    if (!this.employee) return;

    this.isDeleting = true;
    this.deleteError = null;
    this.cdr.detectChanges();

    try {
      await firstValueFrom(this.employeeService.deleteUser(this.employee.id));

      this.showToast('success', 'User deleted successfully.');
      void this.router.navigate(['/employees']);
    } catch {
      this.deleteError = 'Failed to delete user.';
      this.showToast('error', 'Failed to delete user.');
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  cancelChanges(): void {
    if (!this.originalForm) return;

    this.form = { ...this.originalForm };
    this.saveError = null;
  }

  onPhoneLocalNumberInput(value: string): void {
    if (!this.form) return;

    const digitsOnly = (value ?? '').replace(/\D/g, '');
    this.form.phoneLocalNumber = digitsOnly;
  }

  getPhoneLocalInputValue(): string {
    if (!this.form) return '';
    return this.formatPhoneLocalForInput(this.form.phoneLocalNumber, this.form.phoneCountryIso2);
  }

  private formatPhoneLocalForInput(localNumber: string, countryIso2: string | null | undefined): string {
    const digits = (localNumber ?? '').replace(/\D/g, '');

    if (!digits) return '';

    const isNorthAmerica = ['US', 'CA'].includes(String(countryIso2 ?? '').toUpperCase());

    if (isNorthAmerica) {
      const trimmed = digits.slice(0, 10);

      if (trimmed.length <= 3) {
        return trimmed;
      }

      if (trimmed.length <= 6) {
        return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
      }

      return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
    }

    return digits;
  }

  onPhoneCountryChange(value: string | number | null): void {
    if (!this.form) return;
    this.form.phoneCountryIso2 = String(value ?? '').trim().toUpperCase();
  }

  getFormattedPhonePreview(): string {
    const currentForm = this.form;
    if (!currentForm) return '';

    const selectedCountry =
      this.countryPhoneCodes.find((country) => country.iso2 === currentForm.phoneCountryIso2) ?? null;

    const fullPhone = this.employeeService.buildPhoneNumberForApi(
      selectedCountry?.dialCode ?? '1',
      currentForm.phoneLocalNumber,
    );

    return this.employeeService.formatPhoneForDisplay(fullPhone);
  }

  async saveChanges(): Promise<void> {
    if (!this.employee || !this.form || !this.originalForm || !this.isDirty) return;

    this.isSaving = true;
    this.saveError = null;
    this.cdr.detectChanges();

    try {
      const currentForm = this.form;
      const originalForm = this.originalForm;
      const employeeId = this.employee.id;

      const rfidBefore = (originalForm.rfid ?? '').trim();
      const rfidNow = (currentForm.rfid ?? '').trim();
      const rfidChanged = rfidNow !== rfidBefore;

      const wasAdmin = originalForm.adminAccess === 'admin';
      const isAdmin = currentForm.adminAccess === 'admin';
      const adminAccessChanged = wasAdmin !== isAdmin;

      const autoBookingChanged = currentForm.enableAutoBooking !== originalForm.enableAutoBooking;

      const selectedCountry =
        this.countryPhoneCodes.find((country) => country.iso2 === currentForm.phoneCountryIso2) ?? null;

      const phoneNumberForApi = this.employeeService.buildPhoneNumberForApi(
        selectedCountry?.dialCode ?? '1',
        currentForm.phoneLocalNumber,
      );

      const body: PatchUserBody = {
        companyId: currentForm.companyId ?? null,
        email: currentForm.email.trim() || null,
        firstName: currentForm.firstName.trim() || null,
        lastName: currentForm.lastName.trim() || null,
        employeeNumber: currentForm.employeeNumber.trim() || null,
        departmentId: currentForm.departmentId ?? null,
        phoneNumber: phoneNumberForApi,
        reportToId: currentForm.reportToId ?? null,
        status: currentForm.status,
        payGroupId: currentForm.payGroupId ?? null,
        workerTypeId: currentForm.workerTypeId ?? null,
        defaultWorkOrderNumberId: currentForm.defaultWorkOrderNumberId ?? null,
        shiftId: currentForm.shiftId ?? null,
      };

      await firstValueFrom(this.employeeService.patchUser(employeeId, body));

      if (rfidChanged) {
        await firstValueFrom(this.employeeService.assignRfid(employeeId, rfidNow || null));
      }

      if (adminAccessChanged) {
        if (isAdmin) {
          await firstValueFrom(this.employeeService.assignAdminRole(employeeId));
        } else {
          await firstValueFrom(this.employeeService.unassignAdminRole(employeeId));
        }
      }

      if (autoBookingChanged) {
        await firstValueFrom(this.employeeService.toggleAutoBooking(employeeId));
      }

      await this.reloadEmployeeProfile();

      this.saveError = null;
      this.showToast('success', 'Employee updated successfully.');
    } catch {
      this.saveError = 'Failed to save changes.';
      this.showToast('error', 'Failed to save changes.');
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }


  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    const hasOpenModal =
      this.isDeleteModalOpen ||
      this.isAddWonsModalOpen ||
      this.isAddWonsConfirmModalOpen ||
      this.isDeleteWonConfirmModalOpen;

    if (hasOpenModal) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }


  get availableWorkOrders(): WorkOrder[] {
    const assignedIds = new Set(
      this.assignedWorkOrders.map((assignment) => assignment.workOrderNumber.id),
    );
    const searchTerm = this.wonSearchTerm.trim().toLowerCase();

    return this.allWorkOrders
      .filter((workOrder) => !assignedIds.has(workOrder.id))
      .filter((workOrder) => {
        if (!searchTerm) return true;

        const searchable = [
          workOrder.won,
          workOrder.description,
          workOrder.project?.name,
          workOrder.department?.code,
          workOrder.department?.name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchable.includes(searchTerm);
      })
      .sort((a, b) => a.won.localeCompare(b.won, undefined, { numeric: true }));
  }

  get selectedWorkOrderCount(): number {
    return this.selectedWorkOrderIds.size;
  }

  isWorkOrderSelected(workOrderNumberId: number): boolean {
    return this.selectedWorkOrderIds.has(workOrderNumberId);
  }

  toggleWorkOrderSelection(workOrderNumberId: number): void {
    if (this.isAddingWons) return;

    const nextSelection = new Set(this.selectedWorkOrderIds);

    if (nextSelection.has(workOrderNumberId)) {
      nextSelection.delete(workOrderNumberId);
    } else {
      nextSelection.add(workOrderNumberId);
    }

    this.selectedWorkOrderIds = nextSelection;
  }

  openAddWonsModal(): void {
    this.addWonsError = null;
    this.wonSearchTerm = '';
    this.selectedWorkOrderIds = new Set<number>();
    this.isAddWonsModalOpen = true;
    this.isAddWonsConfirmModalOpen = false;
    this.syncBodyScrollLock();
    this.cdr.detectChanges();
  }

  closeAddWonsModal(): void {
    if (this.isAddingWons) return;

    this.isAddWonsModalOpen = false;
    this.isAddWonsConfirmModalOpen = false;
    this.addWonsError = null;
    this.wonSearchTerm = '';
    this.selectedWorkOrderIds = new Set<number>();
    this.syncBodyScrollLock();
    this.cdr.detectChanges();
  }

  openAddWonsConfirmModal(): void {
    if (this.selectedWorkOrderIds.size === 0 || this.isAddingWons) {
      return;
    }

    this.isAddWonsConfirmModalOpen = true;
    this.syncBodyScrollLock();
    this.cdr.detectChanges();
  }

  closeAddWonsConfirmModal(): void {
    if (this.isAddingWons) return;

    this.isAddWonsConfirmModalOpen = false;
    this.syncBodyScrollLock();
    this.cdr.detectChanges();
  }

  get selectedWorkOrders(): WorkOrder[] {
    return this.allWorkOrders
      .filter((workOrder) => this.selectedWorkOrderIds.has(workOrder.id))
      .sort((a, b) => a.won.localeCompare(b.won, undefined, { numeric: true }));
  }

  async confirmAddSelectedWorkOrders(): Promise<void> {
    if (!this.employee || this.selectedWorkOrderIds.size === 0 || this.isAddingWons) {
      return;
    }

    const selectedIds = Array.from(this.selectedWorkOrderIds);
    let successfulAdds = 0;
    let failedAdds = 0;

    this.isAddingWons = true;
    this.addWonsError = null;
    this.cdr.detectChanges();

    for (const workOrderNumberId of selectedIds) {
      try {
        await firstValueFrom(
          this.employeeService.assignWorkOrderToEmployee(
            this.employee.id,
            workOrderNumberId,
          ),
        );
        successfulAdds += 1;
      } catch (error) {
        failedAdds += 1;
        console.error('Failed to assign work order:', {
          employeeId: this.employee.id,
          workOrderNumberId,
          error,
        });
      }
    }

    try {
      await this.reloadAssignedWorkOrders();

      if (failedAdds === 0) {
        this.closeAddWonsModalAfterSave();
        this.showToast(
          'success',
          `${successfulAdds} work order${successfulAdds === 1 ? '' : 's'} assigned successfully.`,
        );
      } else {
        this.isAddWonsConfirmModalOpen = false;
        this.selectedWorkOrderIds = new Set<number>();
        this.addWonsError =
          `${failedAdds} work order${failedAdds === 1 ? '' : 's'} could not be assigned.`;
        this.showToast(
          'error',
          `${successfulAdds} assigned, ${failedAdds} failed.`,
        );
      }
    } finally {
      this.isAddingWons = false;
      this.syncBodyScrollLock();
      this.cdr.detectChanges();
    }
  }

  openDeleteWonConfirmModal(assignment: UserWorkOrderAssignment): void {
    if (this.deletingWorkOrderNumberId !== null) {
      return;
    }

    this.pendingDeleteWorkOrder = assignment;
    this.isDeleteWonConfirmModalOpen = true;
    this.syncBodyScrollLock();
    this.cdr.detectChanges();
  }

  closeDeleteWonConfirmModal(): void {
    if (this.deletingWorkOrderNumberId !== null) {
      return;
    }

    this.isDeleteWonConfirmModalOpen = false;
    this.pendingDeleteWorkOrder = null;
    this.syncBodyScrollLock();
    this.cdr.detectChanges();
  }

  async confirmDeleteAssignedWorkOrder(): Promise<void> {
    const assignment = this.pendingDeleteWorkOrder;

    if (!this.employee || !assignment || this.deletingWorkOrderNumberId !== null) {
      return;
    }

    const workOrderNumberId = assignment.workOrderNumber.id;
    this.deletingWorkOrderNumberId = workOrderNumberId;
    this.cdr.detectChanges();

    try {
      await firstValueFrom(
        this.employeeService.removeWorkOrderFromEmployee(
          this.employee.id,
          workOrderNumberId,
        ),
      );

      this.assignedWorkOrders = this.assignedWorkOrders.filter(
        (item) => item.workOrderNumber.id !== workOrderNumberId,
      );

      this.isDeleteWonConfirmModalOpen = false;
      this.pendingDeleteWorkOrder = null;
      this.showToast('success', `${assignment.workOrderNumber.won} removed successfully.`);
    } catch (error) {
      console.error('Failed to remove assigned work order:', {
        employeeId: this.employee.id,
        workOrderNumberId,
        error,
      });

      this.showToast('error', `Failed to remove ${assignment.workOrderNumber.won}.`);
    } finally {
      this.deletingWorkOrderNumberId = null;
      this.syncBodyScrollLock();
      this.cdr.detectChanges();
    }
  }

  private async reloadAssignedWorkOrders(): Promise<void> {
    this.assignedWorkOrders = await firstValueFrom(
      this.employeeService.getEmployeeAssignedWorkOrders(this.employeeId),
    );
  }

  private closeAddWonsModalAfterSave(): void {
    this.isAddWonsModalOpen = false;
    this.isAddWonsConfirmModalOpen = false;
    this.addWonsError = null;
    this.wonSearchTerm = '';
    this.selectedWorkOrderIds = new Set<number>();
    this.syncBodyScrollLock();
  }

  private showToast(kind: ToastKind, message: string, ms = 2500): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => {
      this.toastMessage = null;
      this.toastTimer = null;
      this.cdr.detectChanges();
    }, ms);

    this.cdr.detectChanges();
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.toastMessage = null;
    this.cdr.detectChanges();
  }

  getStatusClass(status: EmployeeStatus | null | undefined): string {
    return `status-${String(status ?? 'Active').toLowerCase()}`;
  }

  getScheduleRows(): Array<{ dayOfWeek: string; startTime: string; endTime: string; isToday: boolean }> {
    const schedules = this.employee?.departmentScheduleGroup?.scheduleGroup?.schedules ?? [];

    return schedules
      .slice()
      .sort((a, b) => this.dayOfWeekSortIndex(a.dayOfWeek) - this.dayOfWeekSortIndex(b.dayOfWeek))
      .map((schedule) => ({
        dayOfWeek: schedule.dayOfWeek,
        startTime: this.formatTimeHHmm(schedule.startTime),
        endTime: this.formatTimeHHmm(schedule.endTime),
        isToday: schedule.dayOfWeek === this.todayName,
      }));
  }

  get companyName(): string {
    return this.employee?.company?.name ?? '-';
  }

  get departmentName(): string {
    return this.employee?.department?.name ?? '-';
  }

  get workerTypeName(): string {
    return this.employee?.workerType?.name ?? '-';
  }

  get payGroupName(): string {
    return this.employee?.payGroup?.name ?? '-';
  }

  get shiftName(): string {
    return this.employee?.shift?.name ?? '-';
  }

  get shiftPayCodes(): string {
    const payCodes = this.employee?.shift?.payCodes ?? [];
    return payCodes.length ? payCodes.map((payCode) => payCode.name).join(', ') : '-';
  }

  get defaultWONDisplay(): string {
    const won = this.employee?.defaultWorkOrderNumber;
    if (!won) return '-';

    const description = won.description ? ` — ${won.description}` : '';
    return `${won.won}${description}`;
  }

  get departmentOptions(): SearchSelectOption<number>[] {
    return this.departments
      .slice()
      .sort((a, b) => Number(a.code) - Number(b.code))
      .map((department) => ({
        id: department.id,
        label: `${department.code} — ${department.name}`,
      }));
  }

  get companyOptions(): SearchSelectOption<number>[] {
    return this.companies
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((company) => ({
        id: company.id,
        label: company.name,
        subLabel: company.description ?? '',
      }));
  }

  get reportToOptions(): SearchSelectOption<number>[] {
    return this.employeesForReportTo
      .slice()
      .sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''))
      .map((employee) => ({
        id: employee.id,
        label: `${employee.lastName}, ${employee.firstName}`,
        subLabel: employee.email ?? '',
      }));
  }

  get payGroupOptions(): SearchSelectOption<number>[] {
    return this.payGroups.map((payGroup) => ({
      id: payGroup.id,
      label: payGroup.name,
    }));
  }

  get workerTypeOptions(): SearchSelectOption<number>[] {
    return this.workerTypes.map((workerType) => ({
      id: workerType.id,
      label: workerType.name,
    }));
  }

  get shiftOptions(): SearchSelectOption<number>[] {
    return this.shifts
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((shift) => ({
        id: shift.id,
        label: shift.name,
        subLabel: shift.payCodes?.length ? shift.payCodes.map((payCode) => payCode.name).join(', ') : '',
      }));
  }

  get overheadWonOptions(): SearchSelectOption<number>[] {
    return this.overheadWorkOrders.map((workOrder) => ({
      id: workOrder.id,
      label: workOrder.won,
      subLabel: workOrder.description ?? '',
    }));
  }

  get countryPhoneCodeOptions(): SearchSelectOption<string>[] {
    return this.countryPhoneCodes.map((country) => ({
      id: country.iso2,
      label: `+${country.dialCode}`,
      subLabel: `${country.name} (${country.iso2})`,
    }));
  }

  private getLeaveBalanceDisplay(leaveType: string): string {
    const balance = this.employee?.employeeLeaveBalances?.find(
      (item) => String(item.leaveType).toLowerCase() === leaveType.toLowerCase(),
    );

    return balance ? this.formatHours(balance.availableHours) : '-';
  }

  private getLeaveUsedDisplay(leaveType: string): string {
    const balance = this.employee?.employeeLeaveBalances?.find(
      (item) => String(item.leaveType).toLowerCase() === leaveType.toLowerCase(),
    );

    return balance ? this.formatHours(balance.usedHours) : '-';
  }

  getLeavePolicyDisplay(policy: EmployeeLeavePolicyAssignment | null | undefined): string {
    if (!policy?.leavePolicy) return 'Missing';

    return policy.leavePolicy.name;
  }

  getLeavePolicySubtext(policy: EmployeeLeavePolicyAssignment | null | undefined): string {
    if (!policy?.leavePolicy) return 'No policy assigned';

    const startDate = this.formatDate(policy.startDate);
    const hoursPerYear = this.formatHours(policy.leavePolicy.hoursPerYear);
    const maxCarryOver = this.formatHours(policy.leavePolicy.maxCarryOver);

    return `Start: ${startDate} • Hours/Year: ${hoursPerYear} • Max Carryover: ${maxCarryOver}`;
  }

  private formatHours(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '-';
    }

    const rounded = Math.round(Number(value) * 100) / 100;
    return `${rounded.toLocaleString(undefined, {
      minimumFractionDigits: rounded % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    })} hrs`;
  }

  private formatDate(value: string | null | undefined): string {
    if (!value || value.startsWith('0001-01-01')) {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString();
  }

  private formatTimeHHmm(time: string | null | undefined): string {
    if (!time) return '-';

    const [hoursString, minutesString] = time.split(':');
    const hours = Number(hoursString);
    const minutes = Number(minutesString);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return time;
    }

    const amPm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;

    return `${hour12}:${String(minutes).padStart(2, '0')} ${amPm}`;
  }

  private dayOfWeekSortIndex(day: string): number {
    const index = DAY_ORDER.indexOf(day);
    return index === -1 ? 999 : index;
  }

  private readonly employeeColorCount = 8;

  get employeeColorClass(): string {
    return this.getEmployeeColorClassById(this.employee?.id, this.employee?.employeeNumber);
  }

  getEmployeeColorClassById(
    id: number | string | null | undefined,
    employeeNumber?: string | null,
  ): string {
    const raw = Number(id) || Number(employeeNumber) || 0;
    const index = Math.abs(raw) % this.employeeColorCount;

    return `employee-color-${index}`;
  }

  openEmployeeProfile(employeeId: number): void {
    if (!employeeId || employeeId === this.employeeId) {
      return;
    }

    void this.router.navigate(['/employees', employeeId]);
  }
}